import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { EditorState } from 'lexical';
import { useDarkMode } from '../contexts/DarkModeContext';

interface Note {
	note_id: number;
	title: string;
	content: any;
	is_pinned: boolean;
	is_archived: boolean;
	is_password_protected: boolean;
	status: string;
	last_modified_at: string;
	created_at: string;
	tag_ids?: number[];
	tag_names?: string[];
	tag_colors?: string[];
	folder_id?: number | null;
}

interface NoteEditorProps {
	note: Note;
	onSave: (content: any) => void;
	onClose: () => void;
}

const NoteEditor: React.FC<NoteEditorProps> = ({ note, onSave, onClose }) => {
	const { isDark } = useDarkMode();

	const initialConfig = {
		namespace: 'NoteEditor',
		theme: {
			paragraph: `font-mono text-xs leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-800'}`,
			heading: {
				h1: 'text-lg font-mono font-bold uppercase tracking-widest mb-4 mt-6 text-emerald-500',
				h2: 'text-sm font-mono font-bold uppercase tracking-wider mb-3 mt-5 text-emerald-600',
				h3: 'text-xs font-mono font-bold uppercase tracking-wider mb-2 mt-4 text-emerald-700',
			},
			quote: `border-l-2 pl-4 italic my-4 font-mono text-xs ${isDark ? 'border-emerald-500 text-neutral-400' : 'border-emerald-600 text-neutral-600'}`,
			list: {
				ul: 'list-square pl-6 my-2 font-mono text-xs',
				ol: 'list-decimal pl-6 my-2 font-mono text-xs',
				listitem: 'my-1 marker:text-emerald-500',
			},
			link: `underline decoration-emerald-500/50 hover:decoration-emerald-500 transition-colors ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`,
			text: {
				bold: 'font-bold text-emerald-500',
				italic: 'italic opacity-80',
				underline: 'underline decoration-emerald-500/50',
			},
		},
		onError: (error: Error) => {
			console.error('Lexical error:', error);
		},
		editorState: note.content ? JSON.stringify(note.content) : null,
		nodes: [
			HeadingNode,
			QuoteNode,
			ListNode,
			ListItemNode,
			LinkNode,
		],
	};

	const lastSavedContentRef = React.useRef(note.content ? JSON.stringify(note.content) : null);

	const handleChange = (editorState: EditorState) => {
		editorState.read(() => {
			const json = editorState.toJSON();
			const jsonString = JSON.stringify(json);
			
			// Ignore selection changes (clicks/focus) by checking if the actual content JSON changed
			if (jsonString !== lastSavedContentRef.current) {
				lastSavedContentRef.current = jsonString;
				onSave(json);
			}
		});
	};

	return (
		<div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
			{/* Header */}
			<div className={`p-4 border-b ${isDark ? 'border-neutral-800' : 'border-neutral-200'} flex items-center justify-between`}>
				<div className="flex items-center space-x-4">
					{note.is_pinned && (
						<span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${isDark ? 'border-yellow-500/30 text-yellow-500 bg-yellow-500/10' : 'border-yellow-600/30 text-yellow-600 bg-yellow-50'}`}>
							[SYS.PINNED]
						</span>
					)}
					{note.is_password_protected && (
						<span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${isDark ? 'border-emerald-500/30 text-emerald-500 bg-emerald-500/10' : 'border-emerald-600/30 text-emerald-600 bg-emerald-50'}`}>
							[SEC.ENCRYPTED]
						</span>
					)}
				</div>
				<button
					onClick={onClose}
					className={`p-1.5 transition-colors border ${isDark ? 'border-[#333] hover:bg-[#1a1a1a] text-neutral-500 hover:text-white' : 'border-neutral-200 hover:bg-neutral-50 text-neutral-400 hover:text-black'}`}
				>
					<X className="w-4 h-4" />
				</button>
			</div>

			{/* Editor */}
			<div className="flex-1 overflow-y-auto p-6 md:p-8 lg:px-12 relative group">
				<LexicalComposer initialConfig={initialConfig}>
					<div className="relative h-full">
						<RichTextPlugin
							contentEditable={
								<ContentEditable
									className={`min-h-[500px] outline-none focus:outline-none ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}
								/>
							}
							placeholder={
								<div className={`absolute top-0 left-0 pointer-events-none font-mono text-xs uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
									[AWAITING_INPUT...]
								</div>
							}
							ErrorBoundary={LexicalErrorBoundary}
						/>
						<OnChangePlugin onChange={handleChange} />
						<HistoryPlugin />
					</div>
				</LexicalComposer>
			</div>
		</div>
	);
};

export default NoteEditor;
